import { atom, getDefaultStore, useAtom } from 'jotai';
import { Singleton } from 'Helpers/Singleton';
import { UserResourcesApiDataService } from '../MigrationRequests/services/UserResourcesApiDataService';
import { NotificationApiDataService } from './services/NotificationApiDataService';

export class PushNotificationModuleStore extends Singleton {
  private atomStore = getDefaultStore();
  private userResourcesApiDataService: UserResourcesApiDataService;
  private notificationApiDataService: NotificationApiDataService;
  private hasPermissionsAtom;
  private errorAtom;
  private successAtom;
  private loadingAtom;

  constructor() {
    super();

    this.userResourcesApiDataService = new UserResourcesApiDataService();
    this.notificationApiDataService = new NotificationApiDataService();
    this.hasPermissionsAtom = atom(null);
    this.loadingAtom = atom(false);
    this.errorAtom = atom(null);
    this.successAtom = atom(null);
  }

  public Use = () => {
    useAtom(this.hasPermissionsAtom);
    useAtom(this.errorAtom);
    useAtom(this.successAtom);
    useAtom(this.loadingAtom);

    return this;
  };

  public get Error() {
    return this.atomStore.get(this.errorAtom);
  }

  public set Error(message: string | object) {
    this.atomStore.set(this.errorAtom, message);
  }

  public get Success() {
    return this.atomStore.get(this.successAtom);
  }

  public set Success(message: string | object) {
    this.atomStore.set(this.successAtom, message);
  }

  public get HasPermissions(): boolean {
    return this.atomStore.get(this.hasPermissionsAtom);
  }

  public get IsLoading() {
    return this.atomStore.get(this.loadingAtom);
  }

  public set IsLoading(loading: boolean) {
    this.atomStore.set(this.loadingAtom, loading);
  }

  public initialize = async ({ userId }) => {
    this.IsLoading = true;
    const userResources = await this.userResourcesApiDataService.getUserResources({ userId });

    if (userResources) {
      this.atomStore.set(
        this.hasPermissionsAtom,
        userResources?.response?.resources?.includes('Pyx.PyxIQ.PushNotifications'),
      );
    }

    this.IsLoading = false;
  };

  public sendNotifications = async (
    userIds: number[],
    title: string,
    message: string,
    notificationMethodId: number,
  ): Promise<boolean> => {
    this.IsLoading = true;
    this.Error = null;
    this.Success = null;

    if (!userIds || userIds.length === 0) {
      this.Error = 'Please enter at least one User ID.';
      this.IsLoading = false;
      return false;
    }

    if (userIds.length > 500) {
      this.Error = 'Maximum 500 User IDs allowed per request.';
      this.IsLoading = false;
      return false;
    }

    if (!title || title.trim() === '') {
      this.Error = 'Please enter a notification title.';
      this.IsLoading = false;
      return false;
    }

    if (!message || message.trim() === '') {
      this.Error = 'Please enter a notification message.';
      this.IsLoading = false;
      return false;
    }

    const response = await this.notificationApiDataService.sendNotificationToUsers({
      UserIds: userIds,
      BypassScheduleRules: true,
      Title: title,
      Message: message,
      NotificationMethodId: notificationMethodId,
    });

    if (response?.error) {
      this.Error = response?.error;
      this.IsLoading = false;
      return false;
    } else if (response?.success) {
      this.Success = `Successfully sent notifications to ${userIds.length} user(s). Request Correlation ID: ${response?.response?.requestCorrelationId || 'N/A'}`;
    }

    this.IsLoading = false;
    return !!response?.success;
  };

  public clearMessages = () => {
    this.Error = null;
    this.Success = null;
  };
}

